/* SEO component — React 19 hoists title/meta/link to <head> automatically.
 * Provides title, description, OpenGraph, hreflang nl-BE/nl-NL, canonical,
 * and optional JSON-LD schema.
 */
import { useLocation } from "react-router-dom";

const SITE_URL = "https://offertecheck.be";

export default function Seo({
  title,
  description,
  image,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  const location = useLocation();
  const canonical = `${SITE_URL}${location.pathname}`;
  const fullTitle = title?.includes("OfferteExpert") ? title : `${title} | OfferteExpert`;
  const desc =
    description ||
    "Upload je offerte en weet binnen 60 seconden of je te veel betaalt. AI-gedreven offerte-analyse voor België en Nederland.";
  const ogImage =
    image ||
    "https://static.prod-images.emergentagent.com/jobs/41d00f51-e021-4a3a-9144-49fb07005586/images/12f578e968763bf17ce11c6f9432b8ae375fd9af97e20cbf9614a8112a296ebe.png";

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* Hreflang — same Dutch content for BE / NL with x-default */}
      <link rel="alternate" hrefLang="nl-BE" href={canonical} />
      <link rel="alternate" hrefLang="nl-NL" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="OfferteExpert" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="nl_BE" />
      <meta property="og:locale:alternate" content="nl_NL" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </>
  );
}

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "OfferteExpert",
  legalName: "Webtron BV",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  email: "info@offerteexpert.be",
  vatID: "BE0793944703",
  address: {
    "@type": "PostalAddress",
    streetAddress: "A. Rodenbachwijk 12",
    postalCode: "9792",
    addressLocality: "Waregem",
    addressCountry: "BE",
  },
  sameAs: ["https://www.linkedin.com/", "https://www.instagram.com/"],
};

export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "AI offerte-analyse",
  provider: { "@type": "Organization", name: "OfferteExpert", parentOrganization: "Webtron BV" },
  areaServed: ["BE", "NL"],
  offers: [
    {
      "@type": "Offer",
      name: "Eén analyse",
      price: "4.99",
      priceCurrency: "EUR",
      url: `${SITE_URL}/analyse`,
    },
    {
      "@type": "Offer",
      name: "3-pack bundle",
      price: "9.99",
      priceCurrency: "EUR",
      url: `${SITE_URL}/prijzen`,
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "1247",
  },
};

export const buildFaqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map(([q, a]) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const buildArticleSchema = (post, slug) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt,
  image: post.thumbnail,
  datePublished: post.created_at,
  dateModified: post.created_at,
  author: { "@type": "Organization", name: "OfferteExpert" },
  publisher: {
    "@type": "Organization",
    name: "OfferteExpert",
    logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${slug}` },
});
